import { useMemo, useState } from "react";
import { Anchor, Group, Loader, Select, Text } from "@mantine/core";
import { Employee } from "../../types/salon";
import { filterActiveStaff } from "../../utils/staffUtils";

/** Sentinel option value for "clear the commission target". */
const NO_COMMISSION = "__none__";

interface ProductCommissionLineProps {
  /** The product line item's commission target (staff id), or null. */
  staffCommissionTarget: string | null | undefined;
  employees: Employee[];
  /**
   * When false, the line is display-only (the name is plain text, not a link) —
   * gated by the settingsCommissions.canAssignProductCommission permission.
   */
  canEdit: boolean;
  /**
   * Persist a new target: a staff id, or null to unassign. May be async — a
   * spinner shows next to the name until it resolves; if it rejects, the caller
   * surfaces the error (toast) and the displayed name is left unchanged.
   */
  onSave: (staffId: string | null) => void | Promise<void>;
}

/**
 * Inline "Sold by {name}" affordance rendered under a product line item. The
 * commission earner (`staff_commission_target`) is the staff member paid
 * commission on the product sale; a product has no performer, so it is chosen
 * explicitly here.
 *
 * When editable, the name is a teal link; clicking it swaps in a searchable
 * staff picker in place (no modal). Choosing a staff member — or "No
 * commission" — reverts to the name and shows a spinner beside it while
 * `onSave` runs.
 */
export default function ProductCommissionLine({
  staffCommissionTarget,
  employees,
  canEdit,
  onSave,
}: ProductCommissionLineProps) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const targetName = useMemo(() => {
    if (!staffCommissionTarget) return null;
    const emp = (employees || []).find((e) => e.id === staffCommissionTarget);
    return emp ? `${emp.first_name} ${emp.last_name}` : null;
  }, [staffCommissionTarget, employees]);

  // Active staff (plus the current assignee even if they've left) + an explicit
  // "No commission" option, matching the retired picker modal's choices.
  const options = useMemo(
    () => [
      { value: NO_COMMISSION, label: "No commission (unassigned)" },
      ...filterActiveStaff(employees, staffCommissionTarget ?? "").map(
        (emp: Employee) => ({
          value: emp.id,
          label: `${emp.first_name} ${emp.last_name}`,
        }),
      ),
    ],
    [employees, staffCommissionTarget],
  );

  const handleChange = async (value: string | null) => {
    setEditing(false);
    setSaving(true);
    try {
      await onSave(value && value !== NO_COMMISSION ? value : null);
    } catch {
      // The caller surfaces the error (toast); leave the displayed name as-is
      // so the user can click again to retry.
    } finally {
      setSaving(false);
    }
  };

  // Display-only: plain muted text, no link, no picker.
  if (!canEdit) {
    return (
      <Group gap={4} align="center" wrap="nowrap" mt="xs">
        <Text size="xs" c="dimmed">
          Sold by{" "}
          {targetName ?? (
            <Text span size="xs" fs="italic">
              Unassigned
            </Text>
          )}
        </Text>
      </Group>
    );
  }

  return (
    <Group gap={4} align="center" wrap="nowrap" mt="xs">
      <Text size="xs" c="dimmed">
        Sold by
      </Text>
      {editing ? (
        <Select
          data={options}
          defaultValue={staffCommissionTarget ?? NO_COMMISSION}
          onChange={handleChange}
          onDropdownClose={() => setEditing(false)}
          searchable
          autoFocus
          defaultDropdownOpened
          placeholder="Search staff…"
          nothingFoundMessage="No staff found"
          comboboxProps={{ withinPortal: true }}
          size="xs"
          w={220}
          aria-label="Assign product commission"
        />
      ) : (
        <Anchor
          component="button"
          type="button"
          size="xs"
          c="teal"
          underline="hover"
          disabled={saving}
          onClick={() => setEditing(true)}
        >
          {targetName ?? "Unassigned"}
        </Anchor>
      )}
      {saving && <Loader size="xs" aria-label="Saving commission assignment" />}
    </Group>
  );
}
